import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Mail, Loader2, UserMinus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { z } from "zod";

// Email validation schema
const emailSchema = z.string().email("Please enter a valid email address");

interface Member {
  id: string;
  user_id: string;
  org_role: string;
  joined_at: string | null;
  invited_at: string | null;
  profile: {
    name: string;
    avatar_url: string | null;
  } | null;
  email?: string;
}

interface MemberManagementProps {
  organizationId: string;
  currentUserId: string;
}

export function MemberManagement({ organizationId, currentUserId }: MemberManagementProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("MEMBER");
  const [inviting, setInviting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMembers();
  }, [organizationId]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("memberships")
        .select(`
          id,
          user_id,
          org_role,
          joined_at,
          invited_at,
          profile:profiles (
            name,
            avatar_url
          )
        `)
        .eq("organization_id", organizationId)
        .order("joined_at", { ascending: false });

      if (error) throw error;
      setMembers(data as any || []);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    const emailResult = emailSchema.safeParse(inviteEmail.trim());
    if (!emailResult.success) {
      toast({
        variant: "destructive",
        title: "Invalid email",
        description: emailResult.error.errors[0].message,
      });
      return;
    }

    setInviting(true);
    try {
      // Create pending invite in database
      const { error: inviteError } = await supabase
        .from("pending_invites")
        .insert({
          organization_id: organizationId,
          email: inviteEmail.trim().toLowerCase(),
          role: inviteRole as "MEMBER" | "MANAGER" | "ADMIN",
          invited_by: currentUserId,
        });

      if (inviteError) {
        if (inviteError.code === '23505') {
          throw new Error("This email has already been invited.");
        }
        throw inviteError;
      }

      toast({
        title: "Invite sent ✓",
        description: `${inviteEmail} will receive an invitation.`,
      });

      setInviteEmail("");
      setInviteRole("MEMBER");
      setDialogOpen(false);
      fetchMembers();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Invite failed",
        description: error.message || "Could not send invite.",
      });
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (memberId: string, newRole: "MEMBER" | "MANAGER" | "ADMIN" | "OWNER" | "VIEWER") => {
    try {
      const { error } = await supabase
        .from("memberships")
        .update({ org_role: newRole })
        .eq("id", memberId);

      if (error) throw error;

      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, org_role: newRole } : m))
      );

      toast({
        title: "Role Updated",
        description: "Member role has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };

  const handleRemove = async (memberId: string) => {
    try {
      const { error } = await supabase.from("memberships").delete().eq("id", memberId);

      if (error) throw error;

      setMembers((prev) => prev.filter((m) => m.id !== memberId));

      toast({
        title: "Member Removed",
        description: "Member has been removed from the organization.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "OWNER":
        return "bg-primary/10 text-primary";
      case "ADMIN":
        return "bg-secondary/10 text-secondary";
      case "MANAGER":
        return "bg-amber-500/10 text-amber-500";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Card>
      <CardHeader className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div>
            <CardTitle className="text-lg sm:text-xl">Team Members</CardTitle>
            <CardDescription className="text-sm">Manage your organization members and their roles</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto">
                <Plus className="mr-2 h-4 w-4" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Send an invitation to join your organization
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      className="pl-9"
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MEMBER">Member</SelectItem>
                      <SelectItem value="MANAGER">Manager</SelectItem>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">
                    Cancel
                  </Button>
                  <Button onClick={handleInvite} disabled={inviting} className="w-full sm:w-auto">
                    {inviting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Send Invite
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : members.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            No members found
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 rounded-lg border"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-9 w-9 sm:h-10 sm:w-10">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs sm:text-sm">
                      {member.profile?.name?.substring(0, 2).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">{member.profile?.name || "Unknown"}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {member.joined_at
                        ? `Joined ${format(new Date(member.joined_at), "MMM d, yyyy")}`
                        : "Pending invite"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 ml-12 sm:ml-0">
                  {member.user_id === currentUserId ? (
                    <Badge className={`${getRoleBadgeColor(member.org_role)} text-xs`}>
                      {member.org_role} (You)
                    </Badge>
                  ) : (
                    <>
                      <Select
                        value={member.org_role}
                        onValueChange={(value: "MEMBER" | "MANAGER" | "ADMIN") => handleRoleChange(member.id, value)}
                      >
                        <SelectTrigger className="w-24 sm:w-32 h-8 sm:h-10 text-xs sm:text-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MEMBER">Member</SelectItem>
                          <SelectItem value="MANAGER">Manager</SelectItem>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive h-8 w-8 sm:h-10 sm:w-10">
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Member</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to remove this member from the organization?
                              This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRemove(member.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
